import { ReducerCreators } from '@reduxjs/toolkit';

import { IntegrationsState } from './types';
import {
  ApproveRSVPEventLink,
  FetchAvailableInstances,
  FetchRSVPEventLink,
  FetchSolutions,
  ToggleEventIntegration,
} from '@/connection/integrations/integrations';
import toastTrigger from '@ui/toast/toast-trigger';
import { IntegratedEventsDB } from '@/types/integrations.types';
import { EToastTypes } from '@ui/toast/toast.types';

export const reducers = (create: ReducerCreators<IntegrationsState>) => ({
  fetchSolutions: create.asyncThunk(
    async ({ channel }: { channel: number }) => {
      return FetchSolutions(channel);
    },
    {
      fulfilled: (state, action) => {
        state.solutions = action.payload.solutions;
        state.mappedSolutions = action.payload.mappedSolutions;
      },
      pending: (state) => {
        state.loadingSolutions = true;
      },
      rejected: (state, action) => {
        state.loadingSolutions = false;
        console.error(action.error);
      },
    }
  ),
  fetchAvailableInstances: create.asyncThunk(
    async ({ channel, workingProjectUuid }: { channel: number; workingProjectUuid: string }) => {
      return FetchAvailableInstances(channel, workingProjectUuid);
    },
    {
      fulfilled: (state, action) => {
        state.availableInstances = action.payload.all_instances;
        state.loadingIntegrations = false;
      },
      pending: (state) => {
        state.loadingIntegrations = true;
      },
      rejected: (state, action) => {
        state.loadingIntegrations = false;
        console.error(action.error);
        toastTrigger('Could not fetch available integrations', {
          type: EToastTypes.error,
        });
      },
    }
  ),

  fetchRSVPLinkSync: create.asyncThunk(
    async ({ workingProjectUuid }: { workingProjectUuid: string }) => {
      return FetchRSVPEventLink(workingProjectUuid);
    },
    {
      fulfilled: (state, action) => {
        state.rsvpLinkSync = action.payload.rsvpLink;
        state.loadingRSVPLinkSync = false;
      },
      pending: (state) => {
        state.loadingRSVPLinkSync = true;
      },
      rejected: (state, action) => {
        state.loadingRSVPLinkSync = false;
        console.error(action.error);
        toastTrigger('Could not fetch RSVP information', {
          type: EToastTypes.error,
        });
      },
    }
  ),

  toggleEventInstance: create.asyncThunk(
    async ({
      instance_uuid,
      event_uuid,
      channel,
      enable,
    }: {
      instance_uuid: string;
      event_uuid: string;
      channel: number;
      enable: boolean;
    }) => {
      return ToggleEventIntegration(instance_uuid, event_uuid, channel, enable);
    },
    {
      fulfilled: (state, action) => {
        const { added, removed }: { added?: IntegratedEventsDB[]; removed?: IntegratedEventsDB[] } = action.payload;
        if (added?.length) {
          const newInstances = state.availableInstances.map((instance) => {
            if (instance.instance_uuid === added[0].instance_uuid) {
              return {
                ...instance,
                integrations_event: added[0],
              };
            }
            return instance;
          });
          state.availableInstances = newInstances;
        }
        if (removed?.length) {
          const newInstances = state.availableInstances.map((instance) => {
            if (instance.instance_uuid === removed[0].instance_uuid) {
              return {
                ...instance,
                integrations_event: undefined,
              };
            }
            return instance;
          });
          state.availableInstances = newInstances;
        }
      },
      rejected: () => {
        toastTrigger('Could not toggle integration', {
          type: EToastTypes.error,
        });
      },
    }
  ),

  approveRSVPEventLink: create.asyncThunk(
    async ({ linkGroup, eventId }: { linkGroup: string; eventId: number }) => {
      return ApproveRSVPEventLink(linkGroup, eventId);
    },
    {
      fulfilled: (state, action) => {
        state.rsvpLinkSync = action.payload;
      },
      rejected: () => {
        toastTrigger('Could not approve RSVP link', {
          type: EToastTypes.error,
        });
      },
    }
  ),
});
